/**
 * Phase 66 Plan 02 — AuthProvider context.
 *
 * Port of `js/app.js` lines 16-125 (auth IIFE module). React-ifies the
 * stateful pieces (user, login, logout) while preserving the on-disk
 * contract verbatim:
 *
 *   localStorage["protos_token"] — JWT access token (DO NOT RENAME)
 *   localStorage["protos_user"]  — JSON {email, role, role_scopes, circuit_id}
 *
 * Tests seed these via `addInitScript` (helpers.js:seedRole) — renaming
 * either key breaks the entire admin Playwright suite (PATTERNS.md
 * §"Selectors that MUST survive" + 66-TESTID-CONTRACT.md row A1).
 *
 * @see CONTEXT.md D-14 — RBAC UI
 * @see RESEARCH.md §Pitfall 11 — JWT-in-localStorage XSS surface (residual,
 *      mitigated by CSP upstream + JSX default escaping; documented in
 *      threat register T-66-02-02 as "accept").
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { loginApi, logoutApi } from "@/api/auth";
import { authBus } from "./authBus";
import { decodeJwtPayload } from "./jwt";
import { getEffectivePermissions, type Permission } from "./permissions";

const TOKEN_KEY = "protos_token";
const USER_KEY = "protos_user";

export type User = {
  email: string;
  role: string;
  role_scopes: string[];
  circuit_id: string | null;
  /**
   * Phase 69 Plan 06 — D-2FA-01 flag for the Settings 2FA card.
   * Currently NOT carried by the JWT or by /auth/login response — it is
   * persisted to localStorage only when the backend GET /admin-users/me
   * endpoint (Plan 02) is called and surfaces the field. Until then, the
   * card defaults to "Disabilitato" (false). Future plans may hydrate this
   * eagerly on login by extending the /auth/login response shape.
   */
  totp_enabled?: boolean;
};

export type AuthContextValue = {
  user: User | null;
  /** Convenience: server-issued scopes ∪ ROLE_PERMISSIONS[role]. */
  permissions: readonly Permission[];
  /**
   * Phase 69 Plan 06 — `totp_code` is optional. LoginPage passes it on
   * the SECOND submit after the backend returns 401 detail="totp_required"
   * on the first submit. The api/auth.ts wrapper omits the field entirely
   * when undefined, so the backend's normal "no TOTP user" code path is
   * unchanged for legacy logins.
   */
  login: (
    email: string,
    password: string,
    totp_code?: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<AuthContextValue | null>(null);

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<User>;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.email !== "string") return null;
    return {
      email: parsed.email,
      role: typeof parsed.role === "string" ? parsed.role : "admin",
      role_scopes: Array.isArray(parsed.role_scopes) ? parsed.role_scopes : [],
      circuit_id: typeof parsed.circuit_id === "string" ? parsed.circuit_id : null,
      // Phase 69 Plan 06: optional flag. Only carry it forward when the
      // stored payload explicitly sets a boolean; treat anything else as
      // unknown so the Settings card defaults to "Disabilitato".
      totp_enabled:
        typeof parsed.totp_enabled === "boolean"
          ? parsed.totp_enabled
          : undefined,
    };
  } catch {
    return null;
  }
}

function writeStoredUser(user: User) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearStoredAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => readStoredUser());

  // Subscribe to api/client.ts 401 dispatches. authBus decouples this
  // provider from the fetch wrapper (avoids circular import).
  useEffect(() => {
    return authBus.subscribe("logout", () => {
      clearStoredAuth();
      setUser(null);
    });
  }, []);

  const login = useCallback(async (
    email: string,
    password: string,
    totp_code?: string,
  ) => {
    const data = await loginApi(email, password, totp_code);
    const claims = decodeJwtPayload(data.access_token);
    const newUser: User = {
      email,
      // Backend /auth/login returns only {access_token, token_type} — role
      // lives inside JWT claims. Falling back to "admin" hid File Manager
      // + Audit log from super_admin users in Phase 65 (see js/app.js
      // comment lines 72-75); the decoded role is preferred, then top-level
      // `role`, then the safe legacy default.
      role:
        (claims && typeof claims.role === "string" && claims.role) ||
        data.role ||
        "admin",
      role_scopes:
        claims && Array.isArray(claims.role_scopes) ? claims.role_scopes : [],
      circuit_id:
        claims && typeof claims.circuit_id === "string" ? claims.circuit_id : null,
      // Phase 69 live-prod follow-up: backend now echoes totp_enabled on
      // login so the Settings → 2FA card renders the correct badge on
      // first mount. Falls back to undefined for older backends.
      totp_enabled: typeof data.totp_enabled === "boolean"
        ? data.totp_enabled
        : undefined,
    };
    localStorage.setItem(TOKEN_KEY, data.access_token);
    writeStoredUser(newUser);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    // Best-effort backend POST. We swallow errors because the local
    // token is the source of truth for "logged out" UX state.
    try {
      await logoutApi();
    } catch {
      // ignored
    }
    clearStoredAuth();
    setUser(null);
  }, []);

  const permissions = useMemo(
    () => getEffectivePermissions(user?.role, user?.role_scopes),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, permissions, login, logout }),
    [user, permissions, login, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

export { TOKEN_KEY, USER_KEY };
